import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Prompt as RouterPrompt } from "react-router-dom";
import { Modal } from "melodies-source/Modal";
import { Confirmation } from "melodies-source/Confirmation";

type NavigationConfirmHandler = (
  message: string,
  resolve: (confirmed: boolean) => void,
) => void;

let handler: NavigationConfirmHandler | null = null;

export const getUserConfirmation: NavigationConfirmHandler = (
  message,
  resolve,
) => {
  if (handler) {
    handler(message, resolve);
    return;
  }

  resolve(window.confirm(message));
};

interface PromptEntry {
  id: string;
  when: boolean;
  message: string;
  description?: string;
}

interface PromptContext {
  prompts: PromptEntry[];
  addPrompt: (id: string) => void;
  removePrompt: (id: string) => void;
  updatePrompt: (props: PromptEntry) => void;
}

interface PendingNavigation {
  message: string;
  resolve: (confirmed: boolean) => void;
}

const PromptContext = createContext<PromptContext>({
  prompts: [],
  addPrompt: () => {},
  removePrompt: () => {},
  updatePrompt: () => {},
});

export const PromptProvider = ({ children }: PropsWithChildren<{}>) => {
  const [prompts, setPrompts] = useState<PromptEntry[]>([]);
  const [leavingPromptId, setLeavingPromptId] = useState<string | null>(null);
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation | null>(null);

  const addPrompt = useCallback((id: string) => {
    setPrompts((p) => {
      const existing = p.find((entry) => entry.id === id);

      if (!existing) {
        return [{ id, when: false, message: "" }, ...p];
      }

      return p;
    });
  }, []);

  const removePrompt = useCallback((id: string) => {
    setPrompts((p) => p.filter((entry) => entry.id !== id));
  }, []);

  const updatePrompt = useCallback((props: PromptEntry) => {
    setPrompts((p) => {
      return p.map((entry) => {
        if (entry.id === props.id) {
          return props;
        }

        return entry;
      });
    });
  }, []);

  const activePrompt = prompts.find((entry) => entry.when);
  const isLeaving = !!leavingPromptId;
  const currentPrompt = isLeaving ? undefined : activePrompt;
  const hasActivePrompt = !!currentPrompt;

  useEffect(() => {
    handler = (message, resolve) => setPendingNavigation({ message, resolve });

    return () => {
      handler = null;
    };
  }, []);

  useEffect(() => {
    if (!hasActivePrompt) {
      return;
    }

    const confirmUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", confirmUnload);

    return () => window.removeEventListener("beforeunload", confirmUnload);
  }, [hasActivePrompt]);

  useLayoutEffect(() => {
    if (!isLeaving || !pendingNavigation) {
      return;
    }

    pendingNavigation.resolve(true);
    setPendingNavigation(null);
  }, [isLeaving, pendingNavigation]);

  useEffect(() => {
    const hasLeavingPromptUnmounted =
      isLeaving && !prompts.some((entry) => entry.id === leavingPromptId);

    if (hasLeavingPromptUnmounted) {
      setLeavingPromptId(null);
    }
  }, [isLeaving, leavingPromptId, prompts]);

  const leave = () => {
    if (!activePrompt) {
      pendingNavigation?.resolve(true);
      setPendingNavigation(null);
      return;
    }

    setLeavingPromptId(activePrompt.id);
  };

  const stay = () => {
    pendingNavigation?.resolve(false);
    setPendingNavigation(null);
  };

  return (
    <PromptContext.Provider
      value={{ prompts, addPrompt, removePrompt, updatePrompt }}
    >
      {currentPrompt && (
        <RouterPrompt
          when={currentPrompt.when}
          message={currentPrompt.message}
        />
      )}
      {pendingNavigation && (
        <Modal
          isOpen
          onClose={stay}
          header={pendingNavigation.message}
          variant="small"
        >
          <Confirmation
            cancelLabel="Discard Changes"
            confirmLabel="Stay on Page"
            onCancel={leave}
            onConfirm={stay}
          >
            {currentPrompt?.description}
          </Confirmation>
        </Modal>
      )}
      {children}
    </PromptContext.Provider>
  );
};

export const Prompt = ({
  when,
  message,
  description,
}: Omit<PromptEntry, "id">) => {
  const id = useMemo(() => window.crypto.randomUUID(), []);
  const context = useContext(PromptContext);

  useEffect(() => {
    context.addPrompt(id);
    return () => context.removePrompt(id);
  }, [id]);

  useEffect(() => {
    context.updatePrompt({ id, when, message, description });
  }, [id, when, message, description]);

  return null;
};
